/**
 * MA-PHASE11A: Manage Accounts Page
 * 
 * Minimal functional page for viewing account hierarchy and managing sub-accounts.
 * 
 * STRUCTURE:
 * - Current Account
 * - All Accessible Accounts
 * - Child Accounts (if agency)
 */

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTenant } from "@/contexts/TenantContext";
import { usePermissions } from "@/hooks/usePermissions";
import { api } from "@/lib/api";
import { Building2, Plus, Users, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { CreateSubAccountModal } from "@/components/CreateSubAccountModal";
import { toast } from "@/hooks/use-toast";

export default function ManageAccountsPage() {
  // MA-TENANTCONTEXT-ACCOUNT-METADATA-DUPLICATE-FETCH-FIX-002: Use accounts from TenantContext
  const { selectedTenantId, accounts, currentAccount, loading: accountsLoading, refreshAccounts } = useTenant();
  const { effectiveModules, accountName, canManage, canCreateSubaccounts } = usePermissions();
  const navigate = useNavigate();
  
  const [createModalOpen, setCreateModalOpen] = useState(false);

  // MA-TENANTCONTEXT-ACCOUNT-METADATA-DUPLICATE-FETCH-FIX-002: No longer fetching accounts independently
  
  // Derive account categories
  const childAccounts = accounts.filter(a => a.parent_account_id === selectedTenantId);
  const otherAccounts = accounts.filter(a => 
    a.account_id !== selectedTenantId && a.parent_account_id !== selectedTenantId
  );

  // Handle sub-account creation
  const handleSubAccountCreated = async () => {
    // MA-TENANTCONTEXT-ACCOUNT-METADATA-DUPLICATE-FETCH-FIX-002: Use refreshAccounts from TenantContext
    await refreshAccounts();
    
    toast({
      title: "Success",
      description: "Sub-account created successfully",
    });
  };

  if (accountsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-200/70">Loading accounts...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-100">Manage Accounts</h2>
          <p className="text-sm text-slate-200/70 mt-1">
            View and manage your account hierarchy
          </p>
        </div>
        
        {currentAccount?.can_manage && (
          <Button
            onClick={() => setCreateModalOpen(true)}
            data-testid="create-subaccount-page-button"
          >
            <Plus className="h-4 w-4 mr-2" />
            Create Sub-Account
          </Button>
        )}
      </div>

      <Separator className="bg-white/10" />

      {/* Current Account */}
      <Card className="bg-slate-900/50 border-white/10">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Building2 className="h-5 w-5 text-indigo-400" />
            Current Account
          </CardTitle>
        </CardHeader>
        <CardContent>
          {currentAccount ? (
            <div className="flex items-center justify-between p-4 rounded-lg bg-slate-900 border border-indigo-500/30">
              <div>
                <div className="font-medium text-slate-100">{currentAccount.account_name}</div>
                <div className="text-sm text-slate-200/70 mt-1">
                  {currentAccount.role} • {currentAccount.plan || 'Pro'} • {currentAccount.account_type}
                </div>
              </div>
              <Badge variant="outline" className="border-indigo-500/50 text-indigo-300">
                Active
              </Badge>
            </div>
          ) : (
            <div className="text-slate-200/50">No current account selected</div>
          )}
        </CardContent>
      </Card>

      {/* Child Accounts (if agency) */}
      {currentAccount?.account_type === 'agency' && (
        <Card className="bg-slate-900/50 border-white/10">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="h-5 w-5 text-emerald-400" />
              Child Accounts
            </CardTitle>
            <CardDescription className="text-slate-200/70">
              Sub-accounts created under this agency
            </CardDescription>
          </CardHeader>
          <CardContent>
            {childAccounts.length === 0 ? (
              <div className="text-center py-8 text-slate-200/50">
                <Users className="h-12 w-12 mx-auto mb-3 opacity-30" />
                <p>No sub-accounts yet</p>
                {currentAccount?.can_manage && (
                  <Button
                    variant="ghost"
                    className="mt-4"
                    onClick={() => setCreateModalOpen(true)}
                  >
                    Create your first sub-account
                  </Button>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                {childAccounts.map((account) => (
                  <div
                    key={account.account_id}
                    className="flex items-center justify-between p-4 rounded-lg bg-slate-900 border border-white/10 hover:border-emerald-500/30 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-lg bg-emerald-500/20 flex items-center justify-center text-emerald-300">
                        {account.account_name?.charAt(0).toUpperCase() || 'A'}
                      </div>
                      <div>
                        <div className="font-medium text-slate-100">{account.account_name}</div>
                        <div className="text-sm text-slate-200/70">
                          {account.role} • {account.plan || 'Pro'}
                        </div>
                      </div>
                    </div>
                    <ChevronRight className="h-5 w-5 text-slate-200/30" />
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* All Accessible Accounts */}
      <Card className="bg-slate-900/50 border-white/10">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Building2 className="h-5 w-5 text-slate-400" />
            All Accessible Accounts
          </CardTitle>
          <CardDescription className="text-slate-200/70">
            All accounts you have access to
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {accounts.map((account) => (
              <div
                key={account.account_id}
                className={`flex items-center justify-between p-4 rounded-lg border transition-colors ${
                  account.account_id === selectedTenantId
                    ? 'bg-indigo-500/10 border-indigo-500/30'
                    : 'bg-slate-900 border-white/10 hover:border-white/20'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${
                    account.account_id === selectedTenantId
                      ? 'bg-indigo-500/20 text-indigo-300'
                      : 'bg-slate-800 text-slate-300'
                  }`}>
                    {account.account_name?.charAt(0).toUpperCase() || 'A'}
                  </div>
                  <div>
                    <div className="font-medium text-slate-100">{account.account_name}</div>
                    <div className="text-sm text-slate-200/70">
                      {account.role} • {account.plan || 'Pro'} • {account.account_type}
                      {account.parent_account_id && ' • Sub-account'}
                    </div>
                  </div>
                </div>
                {account.account_id === selectedTenantId && (
                  <Badge variant="outline" className="border-indigo-500/50 text-indigo-300">
                    Current
                  </Badge>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Create Sub-Account Modal */}
      <CreateSubAccountModal
        open={createModalOpen}
        onOpenChange={setCreateModalOpen}
        onSuccess={handleSubAccountCreated}
      />
    </div>
  );
}
